import { RefreshCw } from 'lucide-react';
import { AppAlert } from '@/components/AppAlert';
import { Button } from '@/components/ui/button';

/**
 * Someone else changed what this page shows while you have edits of your own open, so the page
 * can't simply reload behind you. Says who, and offers Reload. Without local edits a page reloads
 * by itself (`invalidateLoaders`) and shows nothing. The team hub's `data` and `record` messages
 * (rollout step 2.15) are what will raise it; until then a page can raise it from a 409 stale save.
 */
export function DataChangedBanner({
  changedBy,
  what = 'this',
  hasLocalEdits = false,
  onReload,
  reloading = false,
  onDismiss,
  className,
}: {
  /** Who changed it ("Sam"); left out when unknown. */
  changedBy?: string | null;
  /** What changed, read as "changed <what>" ("this batch", "the description"). */
  what?: string;
  /** Reloading throws away the person's unsaved edits: say so. */
  hasLocalEdits?: boolean;
  onReload: () => void;
  reloading?: boolean;
  /** Keep working on your own copy; the next save may then be refused as stale. */
  onDismiss?: () => void;
  className?: string;
}) {
  const who = changedBy?.trim() || 'Someone';
  return (
    <AppAlert
      role="status"
      tone="warning"
      icon={<RefreshCw className="size-4" />}
      title={`${who} changed ${what}`}
      onDismiss={onDismiss}
      className={className}
      action={
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onReload}
          loading={reloading}
          loadingText="Reloading…"
        >
          Reload
        </Button>
      }
    >
      {hasLocalEdits
        ? 'Reload to see their changes. Your unsaved edits here will be lost.'
        : 'Reload to see their changes.'}
    </AppAlert>
  );
}
